import { z } from 'zod';

/**
 * Shared runtime configuration schema for backend services (controller + worker).
 * Parsed once at bootstrap; invalid config fails fast. Never log the parsed values.
 */
export const backendEnvSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),

  DATABASE_URL: z.string().url().or(z.string().startsWith('postgres')),
  REDIS_URL: z.string().url().or(z.string().startsWith('redis')),

  HTTP_PORT: z.coerce.number().int().positive().default(3000),
  HTTP_HOST: z.string().default('0.0.0.0'),

  /** Comma-separated allowed CORS origins for the web app. */
  CORS_ORIGINS: z.string().default('http://localhost:5173'),

  /** Secret used to sign session cookies / tokens. Must be >= 32 chars in production. */
  SESSION_SECRET: z.string().min(16),

  /** Session lifetime in seconds. */
  SESSION_TTL_SECONDS: z.coerce
    .number()
    .int()
    .positive()
    .default(60 * 60 * 8),

  /** Feature-flag overrides, e.g. "auth.mfa_enforced=true,realtime.sse=false". */
  FEATURE_FLAGS: z.string().default(''),

  /**
   * Master key used to seal PKI private keys (device CA, command-signing) at rest.
   * Falls back to SESSION_SECRET if unset. Interim until the Phase 10 KMS.
   */
  PKI_MASTER_KEY: z.string().min(16).optional(),

  /**
   * Header carrying the URL-encoded client certificate PEM, set by a trusted
   * TLS-terminating proxy that has already verified the mTLS handshake. When unset,
   * the edge channel reads the peer certificate directly from the TLS socket.
   * Only enable behind a trusted proxy — never expose the app directly with this set.
   */
  EDGE_CERT_HEADER: z.string().optional(),

  /**
   * base64 Ed25519 private key (32-byte seed or 64-byte Go key) the controller uses
   * to sign relay tickets, minted by `connaro-relay-keygen`. The public half is
   * configured on every relay. Optional: without it the controller issues no relay
   * tickets, so relayed connections stay pending rather than being brought up — an
   * experimental capability, off until a key is provided.
   */
  CONTROLLER_TICKET_PRIVATE_KEY: z.string().optional(),

  // First-run bootstrap. Off by default and, when on, acts only on an empty
  // install — it provisions the first tenant and platform-admin so a fresh
  // production database has a way in without ever shipping a default credential.
  BOOTSTRAP_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
  BOOTSTRAP_TENANT_SLUG: z.string().min(1).max(63).default('default'),
  BOOTSTRAP_TENANT_NAME: z.string().min(1).max(120).default('Default Organization'),
  BOOTSTRAP_ADMIN_EMAIL: z.string().email().optional(),
  /** Optional: when unset, a strong password is generated and logged once. */
  BOOTSTRAP_ADMIN_PASSWORD: z.string().optional(),
});

export type BackendEnv = z.infer<typeof backendEnvSchema>;

/** Password policy — shared so the API validator and UI hints stay in sync. */
export interface PasswordPolicy {
  minLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireNumber: boolean;
  requireSymbol: boolean;
  /** Reject the N most common passwords / obvious values. */
  rejectCommon: boolean;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  requireLowercase: true,
  requireUppercase: true,
  requireNumber: true,
  requireSymbol: false,
  rejectCommon: true,
};

/** Account-lockout policy after repeated failed logins. */
export interface LockoutPolicy {
  maxFailedAttempts: number;
  windowSeconds: number;
  lockoutSeconds: number;
}

export const DEFAULT_LOCKOUT_POLICY: LockoutPolicy = {
  maxFailedAttempts: 5,
  windowSeconds: 15 * 60,
  lockoutSeconds: 15 * 60,
};
